import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { aiService } from '../../services/aiService';
import { articleGenerationService } from '../../services/articleGenerationService';
import { extractErrorMessageFromResponse } from '../../services/errorService';
import {
  ArticleGenerationCall,
  ArticleGenerationRow,
  ArticleMode,
} from '../../types/articleGeneration';
import { createInitialProgress, GenerationProgress, progressReducer } from './progressReducer';
import { parseSseChunk } from './sseParser';

export function useArticleGeneration(
  companyId: string | null,
  domainId: string | null,
  onError: (message: string) => void
) {
  const [runs, setRuns] = useState<ArticleGenerationRow[]>([]);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [activeMode, setActiveMode] = useState<ArticleMode | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  // The id of the most recently started/streamed generation, exposed so the
  // page can focus the workspace on it as soon as streaming begins.
  const [generationId, setGenerationId] = useState<string | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const generationIdRef = useRef<string | null>(null);
  const { t } = useTranslation();

  const refreshRuns = useCallback(async () => {
    if (!companyId || !domainId) return;
    try {
      setRuns(await articleGenerationService.listGenerations(companyId, domainId));
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [companyId, domainId]);

  useEffect(() => {
    refreshRuns();
  }, [refreshRuns]);

  const patchRunLocally = (id: string, patch: Partial<ArticleGenerationRow>) => {
    setRuns((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const cancel = () => {
    cancelledRef.current = true;
    readerRef.current?.cancel().catch(() => undefined);
  };

  const supabaseSafeCancel = async (id: string) => {
    try {
      const { supabase } = await import('../../services/supabase');
      await supabase.from('article_generations').update({ status: 'cancelled' }).eq('id', id);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const start = async (request: ArticleGenerationCall, mode: ArticleMode, headingCount: number) => {
    setProgress(createInitialProgress(headingCount));
    setActiveMode(mode);
    setIsGenerating(true);
    setElapsedMs(0);
    cancelledRef.current = false;
    generationIdRef.current = 'generationId' in request ? request.generationId : null;
    setGenerationId(generationIdRef.current);
    const startedAt = Date.now();
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);

    try {
      const { data: response, error } = await aiService.generateArticle(request);
      if (error) throw new Error(await extractErrorMessageFromResponse(error, t));
      if (!response.body) throw new Error('No response body received');

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';
      let sawError: string | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const parsed = parseSseChunk(buffer, decoder.decode(value));
        buffer = parsed.rest;
        for (const event of parsed.events) {
          if (event.kind === 'generation') {
            generationIdRef.current = event.id;
            setGenerationId(event.id);
          }
          if (event.kind === 'error') sawError = event.message;
          setProgress((prev) => (prev ? progressReducer(prev, event, Date.now()) : prev));
        }
      }

      if (sawError) onError(sawError);
    } catch (error) {
      if (!cancelledRef.current) {
        Sentry.captureException(error);
        onError(error instanceof Error ? error.message : t('article.generation_error'));
      }
    } finally {
      // The backend already persisted the terminal state (done / incomplete /
      // error). A cancel is client-side, so record it.
      const id = generationIdRef.current;
      if (id && cancelledRef.current) {
        const row = await articleGenerationService.getGeneration(id).catch(() => null);
        if (row && row.status === 'generating') {
          await supabaseSafeCancel(id);
        }
      }
      readerRef.current = null;
      stopTimer();
      setIsGenerating(false);
      await refreshRuns();
    }
  };

  return {
    runs,
    progress,
    activeMode,
    elapsedMs,
    isGenerating,
    generationId,
    start,
    cancel,
    refreshRuns,
    patchRunLocally,
  };
}
